import { useState, useEffect, useRef } from "react";
import { G, PORTFOLIO_API, adminInput } from "../../design/tokens";

const TYPES    = ["Residential", "Commercial", "Agricultural", "Industrial"];
const STATUSES = ["Active Listing", "Under Appraisal", "Title Processing", "Property Managed", "Appraisal Complete"];

const EMPTY_FORM = { title: "", type: "Residential", location: "", area: "", status: "Active Listing", imageUrl: "", imageKey: "", url: "" };

export default function PortfolioModule({ authHeader, canWrite }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);   // null | "add" | "edit"
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editing, setEditing]     = useState(null);   // item being edited
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);   // portfolioId pending delete
  const [err, setErr]             = useState("");
  const [imgFile, setImgFile]     = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(PORTFOLIO_API, { headers: authHeader });
      const d = await r.json();
      setItems(d.items || []);
    } catch {
      setErr("Failed to load portfolio items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setImgFile(null);
    setImgPreview("");
    setEditing(null);
    setErr("");
    setModal("add");
  };

  const openEdit = (item) => {
    setForm({
      title: item.title || "", type: item.type || "Residential",
      location: item.location || "", area: item.area || "",
      status: item.status || "Active Listing",
      imageUrl: item.imageUrl || "", imageKey: item.imageKey || "",
      url: item.url || "",
    });
    setImgFile(null);
    setImgPreview(item.imageUrl || "");
    setEditing(item);
    setErr("");
    setModal("edit");
  };

  const closeModal = () => { setModal(null); setEditing(null); setSaving(false); };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImgPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imgFile) return { imageUrl: form.imageUrl, imageKey: form.imageKey };
    const r = await fetch(`${PORTFOLIO_API}/presign`, {
      method: "POST",
      headers: authHeader,
      body: JSON.stringify({ filename: imgFile.name, contentType: imgFile.type }),
    });
    if (!r.ok) throw new Error("Could not get upload URL");
    const { uploadUrl, key, imageUrl } = await r.json();
    const up = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": imgFile.type }, body: imgFile });
    if (!up.ok) throw new Error("Image upload failed");
    return { imageUrl, imageKey: key };
  };

  const save = async () => {
    if (!form.title.trim()) { setErr("Title is required."); return; }
    setSaving(true);
    setErr("");
    try {
      const { imageUrl, imageKey } = await uploadImage();
      const payload = { ...form, imageUrl, imageKey };
      let r;
      if (modal === "add") {
        r = await fetch(PORTFOLIO_API, { method: "POST", headers: authHeader, body: JSON.stringify(payload) });
      } else {
        r = await fetch(`${PORTFOLIO_API}/${editing.portfolioId}`, { method: "PUT", headers: authHeader, body: JSON.stringify(payload) });
      }
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || "Save failed"); }
      closeModal();
      load();
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (portfolioId) => {
    setDeleting(portfolioId);
    try {
      await fetch(`${PORTFOLIO_API}/${portfolioId}`, { method: "DELETE", headers: authHeader });
      setItems(prev => prev.filter(i => i.portfolioId !== portfolioId));
    } catch {
      setErr("Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ padding: "32px 32px 64px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1 }}>Portfolio</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 4 }}>{items.length} item{items.length !== 1 ? "s" : ""}</div>
        </div>
        {canWrite && (
          <button onClick={openAdd} style={{ background: G.btnGrad, color: "#fff", border: "none", padding: "11px 22px", borderRadius: 10, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: G.btnShadow, letterSpacing: 0.3 }}>
            + Add Item
          </button>
        )}
      </div>

      {err && !modal && (
        <div style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "10px 14px", color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, marginBottom: 18 }}>{err}</div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.3)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, padding: "48px 0", textAlign: "center" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.25)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, padding: "64px 0", textAlign: "center" }}>
          No portfolio items yet. Add your first property.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {items.map(item => (
            <div key={item.portfolioId} style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 14, overflow: "hidden" }}>
              {/* Image */}
              <div style={{ height: 160, background: "rgba(4,10,24,0.6)", position: "relative", overflow: "hidden" }}>
                {item.imageUrl
                  ? <img src={item.imageUrl} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.15)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12 }}>No image</div>
                }
                <div style={{ position: "absolute", top: 10, left: 10, background: "rgba(37,99,235,0.85)", backdropFilter: "blur(6px)", padding: "3px 10px", borderRadius: 5, fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 600, color: "#fff", letterSpacing: 0.8 }}>{item.type}</div>
              </div>
              {/* Info */}
              <div style={{ padding: "16px 18px 14px" }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 4, lineHeight: 1.3 }}>{item.title}</div>
                {item.location && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11.5, color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>📍 {item.location}</div>}
                {item.area     && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11.5, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>📐 {item.area}</div>}
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10.5, color: "#93C5FD", fontWeight: 600, letterSpacing: 0.8, marginBottom: item.url ? 6 : 14 }}>{item.status}</div>
                {item.url && (
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(147,197,253,0.5)", marginBottom: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🔗 {item.url}</div>
                )}
                {canWrite && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => openEdit(item)} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer" }}>Edit</button>
                    <button onClick={() => confirmDelete(item.portfolioId)} disabled={deleting === item.portfolioId} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.2)", background: "rgba(248,113,113,0.07)", color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer" }}>
                      {deleting === item.portfolioId ? "…" : "Delete"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "rgba(11,29,58,0.95)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: "36px 40px", width: "100%", maxWidth: 520, boxShadow: "0 32px 80px rgba(0,0,0,0.6)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 24 }}>
              {modal === "add" ? "Add Portfolio Item" : "Edit Portfolio Item"}
            </div>

            {/* Image upload */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Property Image</div>
              {imgPreview && (
                <div style={{ marginBottom: 10, borderRadius: 8, overflow: "hidden", height: 140, background: "rgba(4,10,24,0.6)" }}>
                  <img src={imgPreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} style={{ display: "none" }} />
              <button onClick={() => fileRef.current?.click()} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer" }}>
                {imgPreview ? "Change Image" : "Choose Image"}
              </button>
              {imgFile && <span style={{ marginLeft: 10, fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{imgFile.name}</span>}
            </div>

            {/* Fields */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Title *</div>
              <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Modern Townhouse Complex" style={adminInput()} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Type</div>
                <select value={form.type} onChange={e => set("type", e.target.value)} style={adminInput()}>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Status</div>
                <select value={form.status} onChange={e => set("status", e.target.value)} style={adminInput()}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Location</div>
              <input value={form.location} onChange={e => set("location", e.target.value)} placeholder="e.g. Quezon City" style={adminInput()} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Area / Size</div>
              <input value={form.area} onChange={e => set("area", e.target.value)} placeholder="e.g. 180 sqm" style={adminInput()} />
            </div>

            <div style={{ marginBottom: 22 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>
                View Details URL <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
              </div>
              <input value={form.url} onChange={e => set("url", e.target.value)} placeholder="https://…" style={adminInput()} />
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.2)", marginTop: 5 }}>When set, "View Details" on the website links here.</div>
            </div>

            {err && <div style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, marginBottom: 16 }}>{err}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={closeModal} style={{ padding: "11px 22px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.5)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: G.btnGrad, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", boxShadow: G.btnShadow, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : modal === "add" ? "Add Item" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
